import styled, { css } from 'styled-components'
import {
	backgroundBlue,
	black,
	blackOrange,
	darkGrey,
	darkOrange,
	dividerBlue,
	gold,
	lightGrey,
	mutedGrey,
	orange,
	vanilla,
	white,
} from '@/theme/colors'
import { media, shadows, motion, radii } from '@/theme/tokens'
import { PageColumn } from '@components/Page'
import { FlowCtaButton } from '@pages/ItineraryImport/ItineraryImportPage.styles'
import { CtaStyle, StepIndicatorState } from './core/presentation'
import { flatButton, TextLinkButton } from './shared.styles'
import { fontSizes, text } from '@/theme/typography'

/**
 * The page column, stretched so the card fills the remaining height with its
 * bottom bar pinned to the card's bottom edge; a tighter floor than the reading
 * pages so that bar stays in reach.
 */
export const FlowContentArea = styled(PageColumn)`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding-bottom: 24px;
`

/* ─── Header row: back slot · title · close ─── */

export const HeaderRow = styled.div`
	display: grid;
	grid-template-columns: 36px 1fr 36px;
	align-items: center;
	padding: 8px 0 16px;

	${media.phone} {
		padding: 0 0 12px;
	}
`

export const HeaderSlot = styled.div`
	width: 36px;
	height: 36px;
`

export const HeaderBackButton = styled.button`
	${flatButton}
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${darkOrange};

	&:hover {
		background: ${darkOrange}14;
	}
`

/* The page heading scale (see PageHeading), centred between the two controls. */
export const HeaderTitle = styled.h1`
	margin: 0;
	text-align: center;
	${text.display}
	color: ${blackOrange};

	${media.phone} {
		font-size: ${fontSizes.headingLg};
	}
`

export const HeaderCloseButton = styled.button`
	${flatButton}
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: ${backgroundBlue};
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${black};

	&:hover {
		filter: brightness(0.96);
	}
`

/* ─── Card ─── */

/* The bento tile's resting surface: same warm shadow, no border. */
export const FlowCard = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	background: ${white};
	border-radius: ${radii.card};
	box-shadow: ${shadows.card};
	overflow: hidden;
`

export const StepIndicator = styled.div`
	display: flex;
	gap: 16px;
	padding: 20px 24px 0;

	${media.phone} {
		gap: 10px;
		padding: 14px 14px 0;
	}
`

/** Rule and label always share a colour — the pair reads as one mark (iOS). */
const stepColor = ({ $state }: { $state: StepIndicatorState }) => {
	switch ($state) {
		case 'active':
			return orange
		case 'completed':
			return gold
		case 'skipped':
			return mutedGrey
		case 'upcoming':
			return darkGrey
		case 'unreachable':
			return lightGrey
	}
}

export const StepTab = styled.button<{ $state: StepIndicatorState }>`
	${flatButton}
	flex: 1;
	min-width: 0;
	text-align: left;
	cursor: ${({ $state }) => ($state === 'active' || $state === 'unreachable' ? 'default' : 'pointer')};
`

export const StepBar = styled.div<{ $state: StepIndicatorState }>`
	height: 4px;
	border-radius: ${radii.round};
	background: ${stepColor};
	transition: background-color ${motion.base};
`

export const StepLabel = styled.div<{ $state: StepIndicatorState }>`
	margin-top: 8px;
	${text.labelSm}
	color: ${stepColor};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	transition: color ${motion.base};

	${media.phone} {
		font-size: ${fontSizes.micro};
	}
`

export const CardContent = styled.div`
	flex: 1;
	padding: 36px 24px 28px;

	${media.phone} {
		padding: 24px 14px 20px;
	}
`

/* ─── Bottom bar ─── */

export const BottomBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 24px;
	background: ${vanilla};
	border-top: 1px solid ${dividerBlue};

	${media.phone} {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
		padding: 12px 14px;
	}
`

export const BarSummary = styled.div`
	min-width: 0;
`

export const BarTitle = styled.div`
	${text.title}
	color: ${blackOrange};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

export const BarMeta = styled.div`
	margin-top: 2px;
	${text.caption}
	color: ${black};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

export const BarActions = styled.div`
	display: flex;
	align-items: center;
	gap: 20px;
	flex-shrink: 0;

	${media.phone} {
		gap: 14px;
	}
`

export const BackLink = styled(TextLinkButton)`
	font-size: ${fontSizes.small};
`

/** Weight the CTA carries: Button's warm primary moves the trip forward; the flat blue passes over the step (iOS). */
const ctaVariant = ({ $variant }: { $variant: CtaStyle }) =>
	$variant === 'secondary' &&
	css`
		background: ${backgroundBlue};
		color: ${black};

		&:hover:not(:disabled) {
			background: ${backgroundBlue};
			filter: brightness(0.96);
		}
	`

/** The card CTA — Next, Create Trip, or the Skip that reads a notch quieter — on the flow CTA the import page shares. */
export const CtaButton = styled(FlowCtaButton)<{ $variant: CtaStyle }>`
	${ctaVariant}

	&:disabled {
		background: ${darkGrey};
		color: ${black};
		opacity: 1;
	}

	${media.phone} {
		flex: 1;
	}
`
